import { useEffect, useState } from 'react';
import styled, { withTheme } from 'styled-components';
import { HeadingTextWrapper } from '../../common/Headings';
import { withAppContext } from 'src/store/initAppContext';
import { useQuery } from '@apollo/client';
import PlayDemoGames from './bingo-slotGames';
import Tabs from './casino-tab';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import config from 'src/config/config';
import Intl from 'src/components/common/Intl';
import SEOContents from 'src/components/SEOContents';
import { Helmet } from "react-helmet";
import { getDamain } from 'src/util/commonfun';
import { MetaProps, SEOFooterCommon, SEOMetaCommon } from 'src/components/SEOMetaCommon';
import { otherGamePagesSEOData } from 'src/config/seo_api';
import Spinner from 'src/components/common/Spinner';
import { GET_NEW_GAMES } from 'src/graphql/menu';

type ApiGame = {
    name?: string;
    code?: string;
    is_prod_live?: number;
    platformName?: string;
    providerName?: string;
    homeImageUrl?: { fun88?: string; common?: string } | null;
    innerImageUrl?: { eng?: string; hin?: string } | null;
    gameRedirectUrl?: string | null;
    isHomepage?: number | null;
};

type ApiPayload = {
    thirdParty?: {
        displayNewGameList?: ApiGame[];
        __typename?: string;
    } | null;
};

const structuredData = (domain: string) =>
    JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                item: `${domain}`,
                name: "Homepage",
                position: 1
            },
            {
                "@type": "ListItem",
                item: `${domain}/new-games`,
                name: "New Games",
                position: 2
            },
        ],
        name: "Breadcrumbs",
    });

const toTitle = (s = "") => s.replace(/-/g, " ").replace(/\s+/g, " ").trim();
const normalizeText = (s: any = "") => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
const nonEmpty = (s?: string | null) => typeof s === "string" && s.trim().length > 0;

const normalizeGamesToGrid = (games: ApiGame[] = []) => {
    let id = 0;
    const seenCodes = new Set<string>();

    const validGames = games.filter((game: any) => {
        return (
            nonEmpty(game?.code) &&
            nonEmpty(game?.name) &&
            nonEmpty(game?.platformName) &&
            nonEmpty(game?.providerName) &&
            nonEmpty(game?.innerImageUrl?.eng) &&
            nonEmpty(game?.innerImageUrl?.hin) &&
            nonEmpty(game?.gameRedirectUrl)
        );
    });

    const uniqueGames = validGames.filter((game: any) => {
        const code = String(game.code).trim();
        const isDuplicate = seenCodes.has(code);

        if (isDuplicate) {
            return false;
        } else {
            seenCodes.add(code);
            return true;
        }
    });

    const normalizedGames = uniqueGames.map((game: any) => {
        id = id + 1;
        const slugName = (game?.name || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        const provideName = (game?.providerName || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
        const gameUrl = game?.gameRedirectUrl.trim();

        return {
            id,
            type: provideName,
            game: toTitle(game.name),
            categoryName: "",
            slug: slugName,
            imgsrc: [
                game.innerImageUrl?.hin,
                game.innerImageUrl?.eng,
            ],
            platformCode: game.platformName,
            code: game.code,
            url: gameUrl
        };
    });

    return normalizedGames;
};

const newgamesicon = `/static/media/newGamesIcon.png`;
let NEWGAMES = <Intl langKey="TITLE@NewGames" />;
const providerTab = [NEWGAMES];
const providerIcons = [{ id: 1, name: NEWGAMES, icon: newgamesicon }];

function NewGames(props: any) {
    const { app: { brandId, currENV }, theme } = props;
    const { showSEOfooterForYolo, showAPISEOData } = theme.config;

    const [games, setGames] = useState<any[]>([]);
    const [filteredGames, setFilteredGames] = useState<any[]>([]);
    const [seoObjects, setSeoObjects] = useState({} as MetaProps);
    const [isLoading, setIsLoading] = useState(true);

    const location = useLocation();
    const [search, setSearch] = useState('');

    const isProd = (currENV === "DEV" || currENV === "STAGING" || currENV === "PRE-PROD") ? 0 : 1;

    const variables: any = { input: { isProd } };
    if (brandId != null && String(brandId).trim() !== "") {
        variables.input.brandId = String(brandId); // API expects String!
    }

    const { data, loading, error } = useQuery<ApiPayload>(GET_NEW_GAMES, {
        variables,
        fetchPolicy: "network-only",
        ssr: false as any,
    });

    useEffect(() => {
        if (loading) return;
        const gamesList = data?.thirdParty?.displayNewGameList || [];
        const gridGames = normalizeGamesToGrid(gamesList);

        setGames(gridGames);
        setFilteredGames(gridGames);
        setIsLoading(false);
    }, [loading, data]);

    useEffect(() => {
        if (error) setIsLoading(false);
    }, [error]);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (showAPISEOData) {
            const getSeoData = otherGamePagesSEOData();
            const pathName = window.location.pathname;

            getSeoData
                .then(resp => {
                    const { objects } = resp;
                    const seoData =
                        objects.find((obj: any) => obj?.metadata?.pathname === pathName) || {};
                    setSeoObjects(seoData?.metadata || ({} as MetaProps));
                })
                .catch(() => { });
        }
    }, [showAPISEOData]);

    useEffect(() => {
        // Expected route: /new-games/:provider/:slug (both segments optional after /new-gamesoptional)
        const pathSegments = location.pathname.split("/").filter(Boolean);
        const newGamesIndex = pathSegments.indexOf("new-games");

        if (newGamesIndex === -1) return; // not on new-games routes

        const providerParam = pathSegments[newGamesIndex + 1] || ""; // ex: "inout"
        const slugParam = pathSegments[newGamesIndex + 2] || ""; // ex: "chicken-royal"

        // If slug exists → search directly
        if (slugParam) {
            handleSearch(slugParam);
            return;
        }

        // If provider exists → filter by provider
        if (providerParam) {
            const filteredGames = games.filter((item: any) => {
                return (
                    normalizeText(item.type) === normalizeText(providerParam) ||
                    normalizeText(item.platformCode) === normalizeText(providerParam) ||
                    normalizeText(item.providerName) === normalizeText(providerParam)
                );
            });

            setFilteredGames(filteredGames);
            return;
        }

        // fallback: show all games
        setFilteredGames(games);
    }, [location.pathname, games]);

    const handleSearch = (searchValue: string) => {
        setSearch(searchValue);

        if (!searchValue) {
            setFilteredGames(games);
            return;
        }

        const searchKey = normalizeText(searchValue);

        const matchedGames = games.filter((item: any) => {
            const searchableText = [
                item.game,
                item.slug,
                item.code,
                item.type,
                item.platformCode,
                item.catagoryName,
            ]
                .filter(Boolean)
                .join(" ");

            const isSearchMatched = normalizeText(searchableText).includes(searchKey);
            return isSearchMatched;
        });

        setFilteredGames(matchedGames);
    };

    const budgetData = () => { };

    const domainURL = getDamain(brandId);

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {showAPISEOData && <SEOMetaCommon metaData={seoObjects} />}
            {!showAPISEOData && <SEOContents pageName={"new-games"} />}
            {!showAPISEOData && (
                <Helmet>
                    <script type="application/ld+json">{structuredData(domainURL)}</script>
                </Helmet>
            )}

            <CasinoWrapper theme={props.app.theme} customStyle={props.theme.image}>
                <TitleHead theme={props.app.theme} customStyle={props.theme.palette}>
                    <Intl langKey="TITLE@NewGames" />
                </TitleHead>

                {isLoading ? (
                    <Spinner />
                ) : (
                    <TabListSection>
                        <Tabs
                            searchText={search}
                            providerIcons={providerIcons}
                            tabs={providerTab}
                            onSearch={handleSearch}
                            isTabsHidden={false}
                            budgetClick={budgetData}
                            minWidthInContent={'initial'}
                            render={(index: any) => (
                                <>
                                    {providerTab[index] === NEWGAMES && <PlayDemoGames data={filteredGames} />}
                                </>
                            )}
                        />
                    </TabListSection>
                )}
            </CasinoWrapper>

            {!showAPISEOData && (showSEOfooterForYolo && !(brandId == 39 || brandId == 40)) && <></>}
            {showAPISEOData && <SEOFooterCommon footer_title={seoObjects['seo_footer_title_(h1)']} footer_content={seoObjects['seo_footer_content']} />}
        </motion.div>
    );
}

export default withTheme(withAppContext(NewGames));

const TabListSection = styled.div`
    width: 100%;
    display: flex;
    align-items: flex-start;
`;

const CasinoWrapper: any = styled.div`
    width: 100%;
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    background-image: url('${(props: any) => `${config.imageV3Host}/static/cashsite/${props.customStyle['homeExchangeBgImage']}`}');
    background-position: 100%;
    background-size: cover;
`;

const TitleHead: any = styled(HeadingTextWrapper)`
    font-size: 28px;
    letter-spacing: 3px;
    text-transform: uppercase;
    white-space: nowrap;
    font-weight: bold;
    color: ${(props: any) => (props.theme === 'Dark' ? '#FFF' : props.customStyle['casinoTitleHeadTextColor'])};
    margin-left: 1em;
    padding-top: 10px;
    position: relative;
    margin-bottom: 10px;
    padding-bottom: 5px;

    :after {
        content: "";
        width: 500px;
        height: 2px;
        background: ${(props: any) => (props.customStyle['casinoTitleHeadBorderBtmLineColor'])};
        position: absolute;
        bottom: 0;
        left: 0;
    }

    @media only screen and (max-width: 992px){
        font-size: 30px;
    }
`;